import { useEffect, useMemo, useRef, useState } from 'react';
import { type Key } from 'react-aria';
import { useProductPeriodicityMap } from '@hungryroot/components-osn';
import { useCustomerDietaryRestrictions } from '@hungryroot/dietary-restrictions';
import { useProducts } from '@hungryroot/products';
import { setOrderDateParam } from '@hungryroot/rest';
import {
  useNextOrderV3,
  useOrderDateParam,
  useProductCategories,
} from '@hungryroot/rest-hooks';

export function useGroceryPreferences() {
  const [selectedCategory, setSelectedCategory] = useState<null | number>(null);

  const cachedProductOrderingPerCategory = useRef(new Map());

  const { data: customerDietaryRestrictionsData } =
    useCustomerDietaryRestrictions();
  const { data: productCategoriesData } = useProductCategories();
  const { data: productPeriodicityMap } = useProductPeriodicityMap();
  const { order } = useNextOrderV3();
  const orderDate = useOrderDateParam(order);

  let queryParams = new URLSearchParams();

  if (customerDietaryRestrictionsData) {
    queryParams.set('categories', String(selectedCategory));
    queryParams.set('limit', '500');

    for (let dietaryRestriction of customerDietaryRestrictionsData.dietary_restrictions) {
      queryParams.set(
        dietaryRestriction.field_name,
        String(dietaryRestriction.selected)
      );
    }
  }

  setOrderDateParam(queryParams, orderDate);

  if (order?.origin) {
    queryParams.set('origin', order.origin.toString());
  }

  useEffect(() => {
    if (productCategoriesData?.results && selectedCategory === null) {
      for (let category of productCategoriesData.results) {
        if (category.id !== 14) {
          setSelectedCategory(category.id);
          break;
        }
      }
    }
  }, [productCategoriesData?.results, selectedCategory]);

  const { data: productsData, isLoading: isProductsLoading } = useProducts({
    options: {
      enabled: Boolean(selectedCategory && customerDietaryRestrictionsData),
    },
    queryParams,
  });

  const groceriesPreferences = useMemo(() => {
    if (!productsData?.pages[0]) return [];

    const transformedGroceryPreferences = productsData.pages[0].results.map(
      (product) => ({
        id: product.id,
        isNew: product.is_new,
        cardImageUrl: product.card_image_url,
        brandName: product.brand_name,
        periodicity: productPeriodicityMap.get(product.id) ?? null,
        name: product.name,
      })
    );

    const groceryPreferencesWithoutNever = transformedGroceryPreferences.filter(
      (item) => item.periodicity !== 1
    );
    const groceryPreferenceswithNever = transformedGroceryPreferences.filter(
      (item) => item.periodicity === 1
    );

    let returnedGroceryPreferences = [
      ...groceryPreferencesWithoutNever,
      ...groceryPreferenceswithNever,
    ];

    if (cachedProductOrderingPerCategory.current.has(selectedCategory)) {
      const cachedProductOrdering =
        cachedProductOrderingPerCategory.current.get(selectedCategory);

      // Since there's a cached ordering of products, use that to reorder the products
      // to match that order.
      returnedGroceryPreferences = returnedGroceryPreferences.sort(
        (firstItem, secondItem) => {
          const firstItemIndex = cachedProductOrdering.indexOf(firstItem.id);
          const secondItemIndex = cachedProductOrdering.indexOf(secondItem.id);

          if (firstItemIndex === -1 && secondItemIndex === -1) {
            return 0;
          } else if (firstItemIndex === -1) {
            return 1;
          } else if (secondItemIndex === -1) {
            return -1;
          } else {
            return firstItemIndex - secondItemIndex;
          }
        }
      );
    } else {
      cachedProductOrderingPerCategory.current.set(
        selectedCategory,
        returnedGroceryPreferences.map((item) => item.id)
      );
    }

    return returnedGroceryPreferences;
  }, [productPeriodicityMap, productsData?.pages, selectedCategory]);

  return {
    onChangeCategory: (tabKey: Key) => {
      const categoryIdFromTabKey = String(tabKey).split('-').pop();
      setSelectedCategory(Number(categoryIdFromTabKey));
    },
    groceries: groceriesPreferences,
    isGroceriesLoading: isProductsLoading,
    selectedCategory,
  };
}
